/**
 * Município: a unidade de federação, o código IBGE e o casamento por nome.
 *
 * POR QUE A RESOLUÇÃO MUNICIPAL É DO OPTEFISCAL, E NÃO DO CONSUMIDOR
 *
 *   O sistema consumidor conhece a empresa: razão social, endereço, cidade. Ele
 *   não conhece — e não tem por que conhecer — o código IBGE de sete dígitos.
 *
 *   Exigi-lo no contrato transferiria ao parceiro uma obrigação que é nossa, e o
 *   resultado prático seria um de dois: ou ele bloqueia o cadastro esperando um
 *   dado que o operador não tem à mão, ou alguém o preenche por aproximação. O
 *   segundo é pior, porque um código IBGE errado não parece errado.
 *
 *   Então o consumidor manda `municipalityName` e `uf`, e a autoridade sobre o
 *   código é de quem emite o documento fiscal.
 *
 * O QUE ESTE MÓDULO É, E O QUE ELE NÃO É
 *
 *   Aqui mora a parte PURA: normalização do nome, o mapa UF↔prefixo e a leitura
 *   do prefixo. A busca no catálogo precisa de banco e vive no motor.
 *
 *   Separar as duas é o que permite provar a regra de casamento — que é onde os
 *   erros moram — sem subir um PostgreSQL.
 *
 * A REGRA QUE NÃO EXISTE AQUI
 *
 *   Não há aproximação. Nem "o mais parecido", nem "o único da UF que começa
 *   igual", nem — sobretudo — "o que outra empresa da mesma cidade usou".
 *
 *   Essa última é a que o legado fazia com provedor, e é o motivo de o §8 do
 *   gate a proibir por escrito: um município resolvido por vizinhança acerta
 *   quase sempre e erra em silêncio no caso que importa.
 */

/**
 * Os dois primeiros dígitos do código IBGE do município são o código da UF.
 *
 * É estrutura do próprio código, e não tabela externa: por isso dá para conferir
 * coerência entre UF declarada e município sem consultar nada.
 *
 * Conferido contra o Anexo A da NFS-e Nacional
 * (`anexo_a-municipio_ibge-paises_iso2-v1-00-snnfse-20251210.xlsx`): das 27
 * siglas, o anexo preenche 7 — RO, AC, AM, RR, PA, AP e TO — e as 7 batem.
 */
export const UF_CODE_BY_ACRONYM: Readonly<Record<string, string>> = Object.freeze({
  RO: '11',
  AC: '12',
  AM: '13',
  RR: '14',
  PA: '15',
  AP: '16',
  TO: '17',
  MA: '21',
  PI: '22',
  CE: '23',
  RN: '24',
  PB: '25',
  PE: '26',
  AL: '27',
  SE: '28',
  BA: '29',
  MG: '31',
  ES: '32',
  RJ: '33',
  SP: '35',
  PR: '41',
  SC: '42',
  RS: '43',
  MS: '50',
  MT: '51',
  GO: '52',
  DF: '53',
});

/** O caminho inverso, derivado — nunca escrito duas vezes. */
export const UF_ACRONYM_BY_CODE: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(Object.entries(UF_CODE_BY_ACRONYM).map(([sigla, codigo]) => [codigo, sigla])),
);

export const UF_ACRONYMS: readonly string[] = Object.freeze(Object.keys(UF_CODE_BY_ACRONYM));

/** A sigla é de duas letras maiúsculas E existe. As duas coisas. */
export function isUfAcronym(valor: unknown): valor is string {
  return typeof valor === 'string' && valor in UF_CODE_BY_ACRONYM;
}

/** O código IBGE do município tem sete dígitos. */
export function isIbgeMunicipalityCode(valor: unknown): valor is string {
  return typeof valor === 'string' && /^[0-9]{7}$/.test(valor);
}

/**
 * A UF que o próprio código do município declara.
 *
 * `null` quando o código é malformado ou quando o prefixo não é de UF nenhuma —
 * e o segundo caso importa: `9912345` tem sete dígitos e não é município.
 */
export function ufFromMunicipalityCode(code: string): string | null {
  if (!isIbgeMunicipalityCode(code)) return null;
  return UF_ACRONYM_BY_CODE[code.slice(0, 2)] ?? null;
}

/**
 * O nome do município, na forma em que se compara.
 *
 * ACENTO, CAIXA E PONTUAÇÃO SAEM; O RESTO FICA
 *
 *   "Araraquara", "ARARAQUARA" e "araraquara" são o mesmo município.
 *   "Santa Bárbara d'Oeste" e "SANTA BARBARA D OESTE" também — e é por isso que
 *   o apóstrofo vira separador em vez de sumir: colar as palavras produziria
 *   "DOESTE", que não casa com nada que alguém digite.
 *
 * POR QUE ISSO É SEGURO
 *
 *   Medido contra as 5.570 linhas do Anexo A: **zero colisões** dentro de uma
 *   mesma UF. Nenhum par de municípios distintos do mesmo estado normaliza para
 *   o mesmo texto, então a normalização não pode escolher o município errado.
 *
 *   Entre UFs diferentes há 232 nomes repetidos — e é exatamente por isso que a
 *   UF é obrigatória no casamento.
 */
export function normalizeMunicipalityName(nome: string): string {
  return nome
    .normalize('NFD')
    /* U+0300–U+036F: os diacríticos que o NFD separou da letra. */
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

export interface MunicipalityMatch {
  readonly code: string;
  readonly name: string;
  readonly uf: string;
}

export type MunicipalityResolution =
  | { readonly outcome: 'resolved'; readonly municipality: MunicipalityMatch }
  | { readonly outcome: 'not_found' }
  | { readonly outcome: 'ambiguous'; readonly candidates: readonly MunicipalityMatch[] };

/**
 * Escolhe entre os candidatos que o catálogo devolveu — ou recusa.
 *
 * Os três desfechos do §8, e nenhum quarto:
 *
 *   0 candidatos   `not_found`   ninguém é inventado
 *   1 candidato    `resolved`
 *   2 ou mais      `ambiguous`   e NÃO se desempata
 *
 * O terceiro caso não deveria acontecer — `(uf, nome normalizado)` é único no
 * catálogo por constraint. Ele existe assim mesmo porque a alternativa é
 * `candidatos[0]`, e `candidatos[0]` é como se escolhe o município errado no dia
 * em que a constraint mudar.
 */
export function resolveMunicipality(
  candidatos: readonly MunicipalityMatch[],
): MunicipalityResolution {
  if (candidatos.length === 0) return { outcome: 'not_found' };
  if (candidatos.length > 1) return { outcome: 'ambiguous', candidates: candidatos };
  return { outcome: 'resolved', municipality: candidatos[0] as MunicipalityMatch };
}
